import { authHeaders, handleUnauthorized } from "@/lib/pin";
import { loadAll } from "@/lib/storage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const BENCH_IDS = ["q1", "q2", "q3", "q5", "q6", "q8", "q9", "q10", "q11", "q12", "q14", "q16", "q17", "q18", "q19", "q20", "q21", "q22", "q23", "q24", "q25", "qt1", "qt2", "qt3"];

export const MIN_REAL = 3;

// Valori di riferimento indicativi per la ristorazione italiana indipendente (uguali per tutte le tipologie):
// usati finché l'archivio reale della tipologia ha meno di MIN_REAL compilazioni.
export const REFERENCE = {
  q9: { opzioni: { "Ce ne occupiamo noi titolari in prima persona": 58, "C'è un responsabile dedicato": 17, "Ci si organizza di volta in volta": 15, "Dipende dal tipo di evento": 10 } },
  q11: { opzioni: { "Ha già lasciato": 8, "Lascia entro un mese": 7, "Lascia entro tre mesi": 10, "Non ci sono ancora date certe": 15, "Lo chef resta, cerchiamo solo supporto": 60 } },
  q12: { media: 3.1 },
  q16: { opzioni: { "Marketing e presenza online": 30, "Organizzazione interna e personale": 28, "Strategia: prezzi, posizionamento, crescita": 18, "Cucina e menù": 9, "Un po' di tutto, serve una regia": 15 } },
  q17: { opzioni: { "Poche ore al mese: preferiamo delegare": 22, "Un incontro a settimana": 46, "Vogliamo essere coinvolti ogni giorno": 20, "Dipende dalla stagione": 12 } },
  q18: { opzioni: { "Fino a 5.000 €": 44, "Tra 5.000 e 15.000 €": 36, "Oltre 15.000 €": 6, "Preferiamo definirlo insieme": 14 } },
  q20: { opzioni: { "Più bassi": 24, "In linea": 46, "Più alti": 12, "Non lo sappiamo con precisione": 18 } },
  q21: { opzioni: { "Sì, con precisione": 14, "Più o meno": 42, "Lo vediamo solo a fine anno": 28, "No": 16 } },
  q22: { media: 2.8 },
  q23: { opzioni: { "Si fermerebbe tutto": 38, "Si andrebbe avanti, ma con difficoltà": 49, "Funzionerebbe quasi normalmente": 13 } },
  q24: { opzioni: { "Sito web aggiornato": 41, "Profili social attivi": 68, "Gestione delle recensioni": 35, "Preventivo scritto standard": 37, "Contratto standard": 24, "Calendario condiviso con lo staff": 29, "Schede costi per evento o menù": 19, "Nessuno di questi": 9 } },
  q25: { opzioni: { "Solo ritocchi: l'impostazione va bene così": 21, "Cambiamenti decisi, ma graduali": 61, "Pronti a stravolgere ciò che non funziona": 18 } },
};

export const toContribution = (sub) => ({
  submission_id: sub.id,
  business_type: sub.businessType,
  answers: Object.fromEntries(BENCH_IDS.filter((k) => sub.answers[k] !== undefined && sub.answers[k] !== "" && !(Array.isArray(sub.answers[k]) && sub.answers[k].length === 0)).map((k) => [k, sub.answers[k]])),
});

export async function contribute(subs) {
  const items = subs.filter((s) => s.status === "completed" && s.businessType).map(toContribution).filter((c) => Object.keys(c.answers).length > 0);
  if (!items.length) return 0;
  const res = await fetch(`${API}/benchmark/contributi`, { method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(items) });
  handleUnauthorized(res);
  if (!res.ok) throw new Error(`Errore del server (${res.status})`);
  return (await res.json()).salvati;
}

export const contributeAllQuiet = () => contribute(loadAll()).catch(() => 0);

export async function fetchBenchmark(type, excludeId) {
  const res = await fetch(`${API}/benchmark/${type}${excludeId ? `?escludi=${excludeId}` : ""}`, { headers: authHeaders() });
  handleUnauthorized(res);
  if (!res.ok) throw new Error(`Errore del server (${res.status})`);
  return res.json();
}

export const statsFor = (bench, qid) => {
  const real = bench?.domande?.[qid];
  if (real && real.n >= MIN_REAL) return { ...real, fonte: "reale" };
  if (REFERENCE[qid]) return { ...REFERENCE[qid], n: real?.n || 0, fonte: "riferimento" };
  return real ? { ...real, fonte: "pochi" } : null;
};

const pct = (s, opt) => s.opzioni?.[opt] ?? 0;

export const insights = (sections, answers, bench) => {
  const out = { distingue: [], linea: [] };
  for (const s of sections) {
    for (const q of s.questions) {
      const v = answers[q.id];
      const st = statsFor(bench, q.id);
      if (v === undefined || v === "" || !st || st.fonte === "pochi") continue;
      if (q.type === "scale" && st.media !== undefined) {
        const d = v - st.media;
        if (Math.abs(d) >= 1) out.distingue.push(`${q.text} ${d > 0 ? "sopra" : "sotto"} la media (${v} contro ${st.media})`);
        else out.linea.push(q.text);
      } else if (q.type === "single" && st.opzioni) {
        const p = pct(st, v);
        if (p <= 20) out.distingue.push(`"${v}": solo il ${p}% delle attività risponde così (${q.text.toLowerCase()})`);
        else if (p >= 40) out.linea.push(q.text);
      } else if (q.type === "multiple" && Array.isArray(v) && st.opzioni) {
        const rare = v.filter((o) => pct(st, o) <= 20 && o !== "Nessuno di questi");
        const missing = Object.entries(st.opzioni).filter(([o, p]) => p >= 55 && !v.includes(o) && o !== "Nessuno di questi").map(([o]) => o);
        if (rare.length) out.distingue.push(`${q.text} Scelte poco comuni: ${rare.join(", ")}`);
        if (missing.length) out.distingue.push(`${q.text} La maggioranza indica anche: ${missing.join(", ")}`);
        if (!rare.length && !missing.length) out.linea.push(q.text);
      } else if (q.type === "number" && st.mediana !== undefined) {
        const r = v / Math.max(st.mediana, 1);
        if (r >= 1.5 || r <= 0.67) out.distingue.push(`${q.text} ${v} ${q.unit} contro una mediana di ${st.mediana}`);
        else out.linea.push(q.text);
      }
    }
  }
  return out;
};
